import mustache from "mustache";
import { serviceProvider } from "../../../comun/provider/ServiceProvider";
import { fx_createFincaClimaHorarioGrpcService } from "../../../modulos/clima/services/FincaClimaHorarioGrpcService";
import { FincaClimaDataHorarioResponse } from "../../../modulos/clima/response/FincaClimaDataHorarioResponse";
import {
  agruparHorarioPorDia,
  climaBackgroundRoute,
  climaCategoriaLabel,
  FincaClimaDataDiarioCalculado,
  isDaylightNow,
} from "../../../utils/climaUtils";
import { formatMesDiaHora, formatMesDiaPadded } from "../../../utils/fechaUtils";
import { _2d } from "../../../utils/numberUtils";
import { ClimaSeccionBase } from "./ClimaSeccionBase";
import template from "./climaHeaderTemplate.html";

mustache.parse(template);

/** Modo del banner: "horario" (Current, hora exacta, usado por `ClimaCurrentPage`)
 *  o "diario" (Forecast, día completo, usado por `ClimaForecastPage`). */
export type ClimaHeaderModo = "horario" | "diario";

/** Cadencia de refresco por modo: la hora cambia más seguido que el día, pero
 *  ninguno necesita más precisión que esta (el peor caso es quedar así de
 *  desactualizado justo al cruzar el límite). */
const REFRESH_INTERVAL_MS: Record<ClimaHeaderModo, number> = {
  horario: 60_000,
  diario: 300_000,
};

let HEADER_ID = 0;

/** Datos vigentes del banner: horario (hora exacta) o diario (día completo,
 *  calculado en el cliente desde el horario — ver `agruparHorarioPorDia`). */
type ClimaHeaderVigente = FincaClimaDataHorarioResponse | FincaClimaDataDiarioCalculado;

/** Banner "hero" de clima: nombre de finca + condición/fecha/temperatura-humedad
 *  de la finca activa, compartido entre `ClimaCurrentPage` (horario) y
 *  `ClimaForecastPage` (diario). Se refresca solo (según `REFRESH_INTERVAL_MS`)
 *  para que el dato "vigente" no quede obsoleto si la página queda abierta. */
export class ClimaHeaderRender extends ClimaSeccionBase<ClimaHeaderVigente> {
  private readonly _headerId: number;

  /** @param _onBack Acción del botón volver (la decide la página: a qué ruta navegar). */
  constructor(
    private readonly _onBack: () => void,
    private readonly _modo: ClimaHeaderModo,
  ) {
    super(REFRESH_INTERVAL_MS[_modo]);
    HEADER_ID += 1;
    this._headerId = HEADER_ID;
  }

  render(): string {
    const finca = serviceProvider.getFincaSelectedService().getRequired();
    const ubicacion = [finca.municipioNombre, finca.departamentoNombre]
      .filter((valor): valor is string => !!valor)
      .join(", ");

    return mustache.render(template, {
      headerId: this._headerId,
      nombreFinca: finca.nombreFinca,
      hasUbicacion: ubicacion.length > 0,
      ubicacion,
    });
  }

  protected override afterConnect(): void {
    document
      .getElementById(`clh-back-${this._headerId}`)
      ?.addEventListener("click", this._onBack);
  }

  protected async fetchList(): Promise<ClimaHeaderVigente[]> {
    const finca = serviceProvider.getFincaSelectedService().getRequired();
    const horas = await fx_createFincaClimaHorarioGrpcService(
      finca.id,
    ).getListAsync();
    return this._modo === "horario" ? horas : agruparHorarioPorDia(horas);
  }

  protected onVigente(vigente: ClimaHeaderVigente): void {
    if (this._modo === "horario") {
      this._renderHorario(vigente as FincaClimaDataHorarioResponse);
    } else {
      this._renderDiario(vigente as FincaClimaDataDiarioCalculado);
    }
  }

  private _renderHorario(current: FincaClimaDataHorarioResponse): void {
    this._setBackground(
      climaBackgroundRoute({
        precipitation: current.precipitation,
        precipitationProbability: current.precipitationProbability,
        totalcloudcover: current.totalcloudcover,
        isdaylight: current.isdaylight,
      }),
    );

    const diaNoche = current.isdaylight === 1 ? "Día" : "Noche";
    const categoria = climaCategoriaLabel(
      current.precipitation,
      current.precipitationProbability,
      current.totalcloudcover,
    );
    this._setCondicion(`${categoria} / ${diaNoche}`);
    this._setFecha(formatMesDiaHora(current.fecha));
    this._setValores(
      `${_2d(current.temperature)} °C | HR ${_2d(current.relativehumidity)} %`,
    );
  }

  private _renderDiario(current: FincaClimaDataDiarioCalculado): void {
    const esDia = isDaylightNow();

    this._setBackground(
      climaBackgroundRoute({
        precipitation: current.precipitationSum,
        precipitationProbability: current.precipitationProbability,
        totalcloudcover: current.totalcloudcover,
        isdaylight: esDia ? 1 : 0,
      }),
    );

    const categoria = climaCategoriaLabel(
      current.precipitationSum,
      current.precipitationProbability,
      current.totalcloudcover,
    );
    this._setCondicion(`${categoria} / ${esDia ? "Día" : "Noche"}`);
    this._setFecha(formatMesDiaPadded(current.fecha));

    // El diario (calculado del horario) no trae un único promedio, solo
    // min/max: el punto medio es una aproximación, no el promedio real del día.
    const temperaturaPromedio =
      (current.temperatureMin + current.temperatureMax) / 2;
    const humedadPromedio =
      (current.relativehumidityMin + current.relativehumidityMax) / 2;
    this._setValores(
      `${_2d(temperaturaPromedio)} °C | HR ${_2d(humedadPromedio)} %`,
    );
  }

  private _setBackground(route: string): void {
    const banner = document.getElementById(`clh-banner-${this._headerId}`);
    if (banner) banner.style.backgroundImage = `url(${route})`;
  }

  private _setCondicion(text: string): void {
    const el = document.getElementById(`clh-condicion-${this._headerId}`);
    if (el) el.textContent = text;
  }

  private _setFecha(text: string): void {
    const el = document.getElementById(`clh-fecha-${this._headerId}`);
    if (el) el.textContent = text;
  }

  private _setValores(text: string): void {
    const el = document.getElementById(`clh-valores-${this._headerId}`);
    if (el) el.textContent = text;
  }
}
